import { useEffect } from 'react';
import type { MyUserDetailsResponse } from 'src/apis/organisation-service/types';
import type {
  OptionalOrganisationServiceRequest,
  OrganisationServiceRequest,
} from 'src/apis/organisation-service/use-organisation-service';
import type { Successful } from 'src/hooks/use-axios';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { handleErrorWithNotification } from 'src/utils/handle-error';

type Options = {
  organisationService: OrganisationServiceRequest;
};

type Params = {
  organisationService: Successful<OrganisationServiceRequest>;
};

export function useMyUserDetails({ organisationService }: Options) {
  const userDetails = useAxios(
    (axios, baseConfig, { organisationService }: Params) =>
      axios.request<MyUserDetailsResponse>({
        ...baseConfig,
        url: organisationService.response.data._links.getMyUserDetails.href,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!organisationService.response) return;
    userDetails.execute({ organisationService });
    // oxlint-disable-next-line exhaustive-deps
  }, [organisationService.response]);

  return userDetails;
}

export type MyUserDetailsRequest = ReturnType<typeof useMyUserDetails>;

export function useOptionalMyUserDetails({
  organisationService,
}: {
  organisationService: OptionalOrganisationServiceRequest;
}) {
  const userDetails = useAxios(
    (
      axios,
      baseConfig,
      {
        organisationService,
      }: { organisationService: Successful<OptionalOrganisationServiceRequest> }
    ) =>
      axios.request<MyUserDetailsResponse>({
        ...baseConfig,
        url: organisationService.response.data._links.getMyUserDetails.href,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (!isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );

  useEffect(() => {
    if (!organisationService.response) return;
    userDetails.execute({ organisationService });
    // oxlint-disable-next-line exhaustive-deps
  }, [organisationService.response]);

  if (!organisationService.response) return;

  return userDetails;
}

export type OptionalMyUserDetailsRequest = ReturnType<
  typeof useOptionalMyUserDetails
>;
